import { useConsumeSearchParam } from '@workspace/ui/hooks/use-consume-search-param'
import { useEffect, useRef, useState } from 'react'
import { useAcceptInviteMutation } from './api'

/**
 * Checks for `?invite=<token>` in the URL and automatically accepts the invite.
 * Param is consumed (removed from URL) on first render.
 */
export function useInviteAcceptance() {
  const inviteToken = useConsumeSearchParam('invite')
  const [status, setStatus] = useState<'idle' | 'accepting' | 'success' | 'error'>('idle')
  const [result, setResult] = useState<{ organizationName: string; role: string } | null>(null)
  const acceptInvite = useAcceptInviteMutation()
  const processedRef = useRef(false)

  // biome-ignore lint/correctness/useExhaustiveDependencies: acceptInvite identity is unstable
  useEffect(() => {
    if (!inviteToken || processedRef.current) return
    processedRef.current = true

    setStatus('accepting')
    acceptInvite.mutate(
      { body: { token: inviteToken } },
      {
        onSuccess: (data) => {
          const name = data.data.startupName ?? data.data.investorName ?? 'the organization'
          setResult({ organizationName: name, role: data.data.role })
          setStatus('success')
        },
        onError: () => {
          setStatus('error')
        },
      }
    )
  }, [inviteToken])

  const dismiss = () => {
    setStatus('idle')
    setResult(null)
  }

  return { status, result, dismiss }
}
